import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  Trash2,
  Terminal,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  HardDrive,
  Camera,
  Network,
  Shield,
  Globe,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import SubscriptionFilter from "@/components/SubscriptionFilter";

const TYPE_META = {
  disks: { icon: HardDrive, label: "Disks", color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-500/10" },
  snapshots: { icon: Camera, label: "Snapshots", color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10" },
  networkInterfaces: { icon: Network, label: "NICs", color: "text-[var(--hig-accent)]", bg: "bg-[var(--hig-accent)]/10" },
  networkSecurityGroups: { icon: Shield, label: "NSGs", color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10" },
  publicIPAddresses: { icon: Globe, label: "Public IPs", color: "text-red-600 dark:text-red-400", bg: "bg-red-500/10" },
};

export default function CleanupWizardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [orphans, setOrphans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [step, setStep] = useState(1); // 1=review, 2=confirm, 3=script
  const [script, setScript] = useState(null);
  const [scriptType, setScriptType] = useState("az_cli");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [includePim, setIncludePim] = useState(false);
  const [pimDuration, setPimDuration] = useState(30);
  const [typeFilter, setTypeFilter] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState(new Set());

  const fetchOrphans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/resources/analysis/orphans?${params}`);
      setOrphans(res.data);
      setSelected(new Set());
      setStep(1);
    } catch (err) {
      toast.error("Failed to detect orphaned resources");
    } finally {
      setLoading(false);
    }
  }, [api, subscriptionId]);

  useEffect(() => {
    fetchOrphans();
  }, [fetchOrphans]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllType = (type) => {
    if (!orphans) return;
    const ids = orphans.orphans.filter((o) => o.type.split("/").pop() === type).map((o) => o.id);
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const generateScript = async () => {
    if (selected.size === 0) return;
    setGenerating(true);
    try {
      const res = await api().post("/resources/generate-script", {
        resource_ids: Array.from(selected),
        script_type: scriptType,
        include_pim: includePim,
        pim_role: "Contributor",
        pim_duration_minutes: pimDuration,
      });
      setScript(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate script");
    } finally {
      setGenerating(false);
    }
  };

  const copyScript = () => {
    if (script?.script) {
      navigator.clipboard.writeText(script.script);
      setCopied(true);
      toast.success("Script copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const selectedOrphans = orphans?.orphans?.filter((o) => selected.has(o.id)) || [];
  const groupedOrphans = {};
  (orphans?.orphans || []).forEach((o) => {
    const short = o.type.split("/").pop();
    if (!groupedOrphans[short]) groupedOrphans[short] = [];
    groupedOrphans[short].push(o);
  });

  const toggleExpand = (type) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  const handleTileClick = (type) => {
    if (typeFilter === type) {
      setTypeFilter(null); // deselect
    } else {
      setTypeFilter(type);
      setExpandedTypes((prev) => new Set([...prev, type])); // auto-expand filtered type
    }
  };

  const filteredGroups = typeFilter
    ? Object.fromEntries(Object.entries(groupedOrphans).filter(([type]) => type === typeFilter))
    : groupedOrphans;

  return (
    <div className="space-y-8" data-testid="cleanup-wizard-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/resources")} className="text-muted-foreground" data-testid="back-to-resources">
          <ArrowLeft className="w-4 h-4 mr-1" /> Resources
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
            Orphan Cleanup Wizard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review orphaned resources, select for cleanup, and generate deletion scripts
          </p>
        </div>
        <SubscriptionFilter value={subscriptionId} onChange={setSubscriptionId} />
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-xs">
        {[
          { num: 1, label: "Review Orphans" },
          { num: 2, label: "Confirm Selection" },
          { num: 3, label: "Execute Script" },
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-2">
            {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
                step === s.num
                  ? "bg-[var(--hig-accent)]/10 text-[var(--hig-accent)] border border-[var(--hig-accent)]/20"
                  : step > s.num
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                    : "text-muted-foreground border border-[var(--n-border-2)]"
              }`}
            >
              <span className="font-mono font-bold">{s.num}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardContent className="p-6"><div className="h-24 bg-[var(--n-border-2)] rounded animate-pulse" /></CardContent>
            </Card>
          ))}
        </div>
      ) : !orphans || orphans.total_orphans === 0 ? (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardContent className="p-12 text-center">
            <Check className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold font-heading">No orphaned resources detected</h3>
            <p className="text-sm text-muted-foreground mt-2">
              All resources have detected associations. Great job keeping things clean!
            </p>
          </CardContent>
        </Card>
      ) : step === 1 ? (
        <>
          {/* Summary tiles - clickable to filter */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card
              className={`cursor-pointer hig-card ${!typeFilter ? "bg-amber-500/10 border-amber-500/30 ring-1 ring-amber-500/20" : "bg-[var(--n-card)] border-[var(--n-border-2)] hover:border-amber-500/20"}`}
              onClick={() => setTypeFilter(null)}
              data-testid="tile-all"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold font-mono text-amber-600 dark:text-amber-400">{orphans.total_orphans}</p>
                <p className="text-xs text-muted-foreground">Total Orphans</p>
              </CardContent>
            </Card>
            {Object.entries(orphans.by_type || {}).map(([type, count]) => {
              const meta = TYPE_META[type] || { icon: AlertTriangle, label: type, color: "text-muted-foreground", bg: "bg-[var(--n-border-2)]" };
              const TypeIcon = meta.icon;
              const isActive = typeFilter === type;
              return (
                <Card
                  key={type}
                  className={`cursor-pointer hig-card ${isActive ? `${meta.bg} border-current ring-1 ring-current/20` : "bg-[var(--n-card)] border-[var(--n-border-2)] hover:border-current/20"}`}
                  onClick={() => handleTileClick(type)}
                  data-testid={`tile-${type}`}
                >
                  <CardContent className="p-4 text-center">
                    <TypeIcon className={`w-4 h-4 ${meta.color} mx-auto mb-1`} />
                    <p className={`text-lg font-bold font-mono ${meta.color}`}>{count}</p>
                    <p className="text-xs text-muted-foreground">{meta.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {typeFilter && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Showing: <Badge variant="outline" className="border-[var(--n-border-2)]">{TYPE_META[typeFilter]?.label || typeFilter}</Badge>
              <button onClick={() => setTypeFilter(null)} className="text-[var(--hig-accent)] hover:underline">Clear filter</button>
            </div>
          )}

          {/* Grouped by type - collapsed by default */}
          <div className="space-y-3">
            {Object.entries(filteredGroups).map(([type, items]) => {
              const meta = TYPE_META[type] || { icon: AlertTriangle, label: type, color: "text-muted-foreground", bg: "bg-[var(--n-border-2)]" };
              const TypeIcon = meta.icon;
              const allSelected = items.every((o) => selected.has(o.id));
              const selectedCount = items.filter((o) => selected.has(o.id)).length;
              const isExpanded = expandedTypes.has(type);
              return (
                <Card key={type} className="bg-[var(--n-card)] border-[var(--n-border-2)]" data-testid={`group-${type}`}>
                  <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleExpand(type)}>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <TypeIcon className={`w-4 h-4 ${meta.color}`} />
                        {meta.label} ({items.length})
                        {selectedCount > 0 && (
                          <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0 text-[10px]">{selectedCount} selected</Badge>
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); selectAllType(type); }}
                          className={`text-xs ${allSelected ? "text-[var(--hig-accent)]" : "text-muted-foreground"}`}
                          data-testid={`select-all-${type}`}
                        >
                          {allSelected ? "Deselect All" : "Select All"}
                        </Button>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    </div>
                  </CardHeader>
                  {isExpanded && (
                    <CardContent className="space-y-1.5 pt-3">
                      {items.map((o) => (
                        <div
                          key={o.id}
                          className={`flex items-start gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer ${
                            selected.has(o.id)
                              ? "bg-amber-500/5 border-amber-500/20"
                              : "bg-[var(--n-bg)] border-[var(--n-border-2)] hover:border-[var(--n-border-3)]"
                          }`}
                          onClick={() => toggleSelect(o.id)}
                          data-testid={`orphan-item-${o.id}`}
                        >
                          <Checkbox
                            checked={selected.has(o.id)}
                            className="mt-0.5 border-[var(--n-border-3)] data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{o.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{o.reason}</p>
                            <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                              <span>RG: {o.resource_group}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>

          <div className="flex items-center justify-between sticky bottom-0 bg-[var(--n-bg)] p-4 -mx-4 border-t border-[var(--n-border-2)] rounded-b-lg">
            <span className="text-sm text-muted-foreground">
              {selected.size} resource{selected.size !== 1 ? "s" : ""} selected
            </span>
            <Button
              onClick={() => setStep(2)}
              disabled={selected.size === 0}
              className="bg-amber-500 hover:bg-amber-600 text-primary-foreground font-semibold"
              data-testid="proceed-to-confirm"
            >
              Review Selection <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </>
      ) : step === 2 ? (
        <>
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                Confirm Resources for Cleanup ({selected.size})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[40vh] overflow-y-auto">
              {selectedOrphans.map((o) => (
                <div key={o.id} className="flex items-center gap-3 p-2 rounded bg-[var(--n-bg)] text-xs">
                  <Trash2 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                  <span className="text-foreground font-medium truncate">{o.name}</span>
                  <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)] text-muted-foreground shrink-0">
                    {o.type.split("/").pop()}
                  </Badge>
                  <span className="text-muted-foreground ml-auto shrink-0">{o.resource_group}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Script Type</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={scriptType} onValueChange={setScriptType}>
                <TabsList className="bg-[var(--n-bg)] border border-[var(--n-border-2)]">
                  <TabsTrigger value="az_cli" className="text-xs data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]" data-testid="script-type-azcli">
                    <Terminal className="w-3.5 h-3.5 mr-1.5" /> Azure CLI
                  </TabsTrigger>
                  <TabsTrigger value="powershell" className="text-xs data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]" data-testid="script-type-powershell">
                    <Terminal className="w-3.5 h-3.5 mr-1.5" /> PowerShell
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </CardContent>
          </Card>

          {/* PIM JIT Elevation */}
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  PIM Just-In-Time Elevation
                </CardTitle>
                <Checkbox
                  id="pim-toggle"
                  checked={includePim}
                  onCheckedChange={(c) => setIncludePim(c)}
                  className="border-[var(--n-border-3)] data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  data-testid="pim-toggle"
                />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                Prepend PIM role activation to the script. The Contributor role will be elevated for the selected duration before any deletions run.
              </p>
              {includePim && (
                <div className="mt-3 flex items-center gap-3">
                  <label className="text-xs text-muted-foreground">Duration:</label>
                  <div className="flex gap-1.5">
                    {[
                      { value: 15, label: "15 min" },
                      { value: 30, label: "30 min" },
                      { value: 60, label: "1 hour" },
                      { value: 120, label: "2 hours" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setPimDuration(opt.value)}
                        className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                          pimDuration === opt.value
                            ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                            : "text-muted-foreground border border-[var(--n-border-2)] hover:text-foreground"
                        }`}
                        data-testid={`pim-duration-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="border-[var(--n-border-2)]" data-testid="back-to-review">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              onClick={generateScript}
              disabled={generating}
              className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
              data-testid="generate-script-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Terminal className="w-4 h-4 mr-2" /> Generate Script</>
              )}
            </Button>
          </div>
        </>
      ) : step === 3 && script ? (
        <>
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-[var(--hig-accent)]" />
                  {scriptType === "az_cli" ? "Azure CLI" : "PowerShell"} Cleanup Script ({script.resource_count} resources)
                  {includePim && (
                    <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[10px]">
                      <Shield className="w-2.5 h-2.5 mr-0.5" /> PIM {pimDuration}m
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyScript}
                    className="border-[var(--n-border-2)] text-xs"
                    data-testid="copy-script-btn"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => window.open(script.cloud_shell_url, "_blank")}
                    className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold text-xs"
                    data-testid="open-cloud-shell-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Azure Cloud Shell
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-600 dark:text-amber-400 mb-4 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>This script has DRY_RUN enabled by default. Review the script carefully and set DRY_RUN to false only when you are ready to delete resources.</span>
              </div>
              <pre className="bg-[var(--n-bg)] border border-[var(--n-border-2)] rounded-lg p-4 text-xs font-mono text-foreground/90 overflow-x-auto max-h-[50vh] whitespace-pre-wrap" data-testid="script-content">
                {script.script}
              </pre>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex gap-3">
              <span>1. Copy the script</span>
              <ChevronRight className="w-3 h-3" />
              <span>2. Open Azure Cloud Shell</span>
              <ChevronRight className="w-3 h-3" />
              <span>3. Paste and review</span>
              <ChevronRight className="w-3 h-3" />
              <span>4. Set DRY_RUN=false to execute</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setStep(2)} className="border-[var(--n-border-2)]" data-testid="back-to-confirm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              variant="outline"
              onClick={() => { setScriptType(scriptType === "az_cli" ? "powershell" : "az_cli"); setStep(2); }}
              className="border-[var(--n-border-2)]"
              data-testid="switch-script-type"
            >
              <Terminal className="w-4 h-4 mr-1" /> Switch to {scriptType === "az_cli" ? "PowerShell" : "Azure CLI"}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
